import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { NofeeNavLogo } from './NoFeeLogo';
import { ArrowLeft, Mail, MapPin } from 'lucide-react';

const COMPANY = {
  name: 'NoFee LLC',
  address: '9100 Conroy Road, Suite 200',
  city: 'Windermere, FL 34786',
  country: 'United States',
  email: 'support@nofee.restaurant',
  domain: 'nofee.restaurant',
};

/**
 * Reusable legal page chrome — sticky nav, prose-style body, contact footer.
 * Pass title + lastUpdated + children (the actual legal text).
 */
const LegalLayout = ({ title, lastUpdated, children }) => {
  useEffect(() => {
    document.title = `${title} · NoFee.restaurant`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Nav */}
      <nav className="sticky top-0 z-30 bg-black/85 backdrop-blur-md border-b border-gold-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-gold-400 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <Link to="/">
            <NofeeNavLogo height={32} />
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <header className="mb-12 pb-8 border-b border-gold-900/30">
          <p className="text-gold-400 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight" data-testid="legal-page-title">
            {title}
          </h1>
          <p className="mt-4 text-sm text-white/40">
            Last updated: <span className="text-white/70 font-semibold">{lastUpdated}</span>
          </p>
        </header>

        <div className="legal-prose text-white/75 leading-relaxed text-base sm:text-[17px] space-y-6">
          {children}
        </div>

        {/* Contact footer */}
        <footer className="mt-16 pt-8 border-t border-gold-900/30">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gold-400 mb-3">Questions?</h3>
          <div className="space-y-2 text-sm text-white/60">
            <p className="font-semibold text-white">{COMPANY.name}</p>
            <p className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-gold-400 shrink-0" />
              <span>
                {COMPANY.address}
                <br />
                {COMPANY.city}
                <br />
                {COMPANY.country}
              </span>
            </p>
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gold-400" />
              <a href={`mailto:${COMPANY.email}`} className="text-gold-300 hover:text-gold-200 transition-colors">
                {COMPANY.email}
              </a>
            </p>
          </div>
        </footer>
      </article>
    </div>
  );
};

export const LegalH2 = ({ children, id }) => (
  <h2 id={id} className="scroll-mt-20 text-2xl sm:text-3xl font-black text-white mt-12 mb-4">
    {children}
  </h2>
);

export const LegalH3 = ({ children }) => (
  <h3 className="text-xl font-bold text-gold-300 mt-8 mb-3">{children}</h3>
);

export const LegalUL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 marker:text-gold-500">{children}</ul>
);

export const LegalP = ({ children }) => <p>{children}</p>;

export { COMPANY };
export default LegalLayout;
